import { EdgelessLegacySlotIdentifier } from '@blocksuite/hive/blocks/surface';
import {
  Bound,
  getCommonBoundWithRotation,
} from '@blocksuite/hive/global/gfx';
import type { RootBlockModel } from '@blocksuite/hive/model';
import {
  MOUSE_BUTTON,
  requestConnectedFrame,
} from '@blocksuite/hive/shared/utils';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/hive/std';
import { GfxControllerIdentifier } from '@blocksuite/hive/std/gfx';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/dom';
import { effect } from '@preact/signals-core';
import { css, html, nothing } from 'lit';
import { query, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { literal, unsafeStatic } from 'lit/static-html.js';

import type { AIItemGroupConfig } from '../../components/ai-item/types.js';
import {
  HIVE_AI_PANEL_WIDGET,
  HiveAIPanelWidget,
} from '../ai-panel/ai-panel.js';
import { EdgelessCopilotPanel } from '../edgeless-copilot-panel/index.js';

export const HIVE_EDGELESS_COPILOT_WIDGET = 'hive-edgeless-copilot-widget';

export class EdgelessCopilotWidget extends WidgetComponent<RootBlockModel> {
  static override styles = css`
    .copilot-selection-rect {
      position: absolute;
      box-sizing: border-box;
      border-radius: 4px;
      border: 2px dashed var(--hive-brand-color, #1e96eb);
    }
  `;

  private _clickOutsideOff: (() => void) | null = null;

  private _copilotPanel!: EdgelessCopilotPanel | null;

  private _listenClickOutsideId: number | null = null;

  private _selectionModelRect!: DOMRect;

  groups: AIItemGroupConfig[] = [];

  get gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  get selectionModelRect() {
    return this._selectionModelRect;
  }

  get selectionRect() {
    return this._selectionRect;
  }

  get visible() {
    return !!(
      this._visible &&
      this._selectionRect.width &&
      this._selectionRect.height
    );
  }

  set visible(visible: boolean) {
    this._visible = visible;
  }

  private _showCopilotInput() {
    requestConnectedFrame(() => {
      const referenceElement = this.selectionElem;
      if (!referenceElement || !referenceElement.isConnected) return;

      // show ai input
      const rootBlockId = this.host.doc.root?.id;
      if (!rootBlockId) return;

      const input = this.host.view.getWidget(
        HIVE_AI_PANEL_WIDGET,
        rootBlockId
      );

      if (input instanceof HiveAIPanelWidget) {
        input.setState('input', referenceElement);
        requestAnimationFrame(() => {
          this._createCopilotPanel();
          this._updateCopilotPanel(input);
        });
      }
    }, this);
  }

  private _createCopilotPanel() {
    if (!this._copilotPanel) {
      const panel = new EdgelessCopilotPanel();
      panel.host = this.host;
      panel.groups = this.groups;
      this.renderRoot.append(panel);
      this._copilotPanel = panel;
    }
  }

  private _updateCopilotPanel(referenceElement: HTMLElement) {
    // @TODO: optimize
    const viewport = this.gfx.viewport;
    const panel = this._copilotPanel;
    if (!panel) return;
    autoUpdate(referenceElement, panel, () => {
      computePosition(referenceElement, panel, {
        placement: 'bottom-start',
        middleware: [
          offset({
            mainAxis: 4,
          }),
          flip({
            mainAxis: true,
            crossAxis: true,
            flipAlignment: true,
          }),
          shift(() => {
            const { left, top, width, height } = viewport;
            return {
              padding: 20,
              crossAxis: true,
              rootBoundary: {
                x: left,
                y: top,
                width,
                height: height - 100,
              },
            };
          }),
          size({
            apply: ({ elements }) => {
              const { height } = viewport;
              elements.floating.style.maxHeight = `${height - 140}px`;
            },
          }),
        ],
      })
        .then(({ x, y }) => {
          panel.style.left = `${x}px`;
          panel.style.top = `${y}px`;
        })
        .catch(e => {
          console.warn("Can't compute EdgelessCopilotPanel position", e);
        });
    });
  }

  private _updateSelection(rect: DOMRect) {
    this._selectionModelRect = rect;

    const zoom = this.gfx.viewport.zoom;
    const [x, y] = this.gfx.viewport.toViewCoord(rect.left, rect.top);
    const [width, height] = [rect.width * zoom, rect.height * zoom];

    this._selectionRect = { x, y, width, height };
  }

  private _watchClickOutside() {
    this._clickOutsideOff?.();

    const { width, height } = this._selectionRect;

    if (width && height) {
      this._listenClickOutsideId &&
        cancelAnimationFrame(this._listenClickOutsideId);
      this._listenClickOutsideId = requestConnectedFrame(() => {
        if (!this.isConnected) {
          return;
        }

        const off = this.std.event.add('pointerDown', ctx => {
          const e = ctx.get('pointerState').raw;
          if (
            e.button === MOUSE_BUTTON.MAIN &&
            !this.contains(e.target as HTMLElement)
          ) {
            off();
            this._visible = false;
            this.hideCopilotPanel();
          }
        });
        this._listenClickOutsideId = null;
        this._clickOutsideOff = off;
      }, this);
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();

    const CopilotSelectionTool = this.gfx.tool.get('copilot');

    this._disposables.add(
      CopilotSelectionTool.draggingAreaUpdated.subscribe(shouldShowPanel => {
        this._visible = true;
        this._updateSelection(CopilotSelectionTool.area);
        if (shouldShowPanel) {
          this._showCopilotInput();
          this._watchClickOutside();
        } else {
          this.hideCopilotPanel();
        }
      })
    );

    this._disposables.add(
      this.gfx.viewport.viewportUpdated.subscribe(() => {
        if (!this._visible) return;

        this._updateSelection(CopilotSelectionTool.area);
      })
    );

    this._disposables.add(
      effect(() => {
        const currentTool = this.gfx.tool.currentToolName$.value;

        if (!this._visible || currentTool === 'copilot') return;

        this._visible = false;
        this._clickOutsideOff = null;
        this._copilotPanel?.remove();
        this._copilotPanel = null;
      })
    );
  }

  determineInsertionBounds(width = 800, height = 95) {
    const elements = this.gfx.selection.selectedElements;
    const offsetY = 20 / this.gfx.viewport.zoom;
    const bounds = new Bound(0, 0, width, height);
    if (elements.length) {
      const { x, y, h } = getCommonBoundWithRotation(elements);
      bounds.x = x;
      bounds.y = y + h + offsetY;
    } else {
      const { x, y, height: h } = this.selectionModelRect;
      bounds.x = x;
      bounds.y = y + h + offsetY;
    }
    return bounds;
  }

  hideCopilotPanel() {
    this._copilotPanel?.hide();
    this._copilotPanel = null;
    this._clickOutsideOff = null;
  }

  lockToolbar(disabled: boolean) {
    const legacySlot = this.std.get(EdgelessLegacySlotIdentifier);
    legacySlot.toolbarLocked.next(disabled);
  }

  override render() {
    if (!this._visible) return nothing;

    const rect = this._selectionRect;

    return html`<div class="hive-edgeless-ai">
      <div
        class="copilot-selection-rect"
        style=${styleMap({
          left: `${rect.x}px`,
          top: `${rect.y}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
        })}
      ></div>
    </div>`;
  }

  @state()
  private accessor _selectionRect: {
    x: number;
    y: number;
    width: number;
    height: number;
  } = { x: 0, y: 0, width: 0, height: 0 };

  @state()
  private accessor _visible = false;

  @query('.copilot-selection-rect')
  accessor selectionElem!: HTMLDivElement;
}

export const edgelessCopilotWidget = WidgetViewExtension(
  'hive:page',
  HIVE_EDGELESS_COPILOT_WIDGET,
  literal`${unsafeStatic(HIVE_EDGELESS_COPILOT_WIDGET)}`
);

declare global {
  interface HTMLElementTagNameMap {
    [HIVE_EDGELESS_COPILOT_WIDGET]: EdgelessCopilotWidget;
  }
}
